import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type CategoryMeta } from '../api.ts';
import type { ArticleSummary, Category, ContentStatus } from '../../../shared/types.ts';

const STATUS_ICON: Record<ContentStatus, string> = {
  published: '✓',
  draft: '◐',
  planned: '○',
};

export function RoadmapPage(): JSX.Element {
  const [articles, setArticles] = useState<ArticleSummary[] | null>(null);
  const [cats, setCats] = useState<Record<Category, CategoryMeta> | null>(null);
  const [filter, setFilter] = useState<'all' | ContentStatus | Category>('all');

  useEffect(() => {
    api.listAll().then(d => { setArticles(d.articles); setCats(d.categories); });
  }, []);

  const filtered = useMemo(() => {
    if (!articles) return [];
    if (filter === 'all') return articles;
    if (filter === 'published' || filter === 'draft' || filter === 'planned') {
      return articles.filter(a => a.status === filter);
    }
    return articles.filter(a => a.category === filter);
  }, [articles, filter]);

  if (!articles || !cats) return <div className="center-pad"><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  const counts = {
    all: articles.length,
    published: articles.filter(a => a.status === 'published').length,
    draft: articles.filter(a => a.status === 'draft').length,
    planned: articles.filter(a => a.status === 'planned').length,
  };

  return (
    <div className="page">
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-faint)' }}>
        <Link to="/">Home</Link> / Roadmap
      </div>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 38px)', margin: '0 0 8px', letterSpacing: '-.02em' }}>The roadmap</h1>
      <p style={{ color: 'var(--text-dim)', maxWidth: 700, fontSize: 17, marginBottom: 18 }}>
        Every article we plan to write, the ones in progress, and what's already shipped. The list updates as we ship.
      </p>

      <div className="roadmap-tabs">
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All ({counts.all})</button>
        <button className={filter === 'published' ? 'on' : ''} onClick={() => setFilter('published')}>✓ Published ({counts.published})</button>
        <button className={filter === 'draft' ? 'on' : ''} onClick={() => setFilter('draft')}>◐ Draft ({counts.draft})</button>
        <button className={filter === 'planned' ? 'on' : ''} onClick={() => setFilter('planned')}>○ Planned ({counts.planned})</button>
        {(Object.keys(cats) as Category[]).map(c => (
          <button key={c} className={filter === c ? 'on' : ''} onClick={() => setFilter(c)}>
            {cats[c].label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18, background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 14 }}>
        {filtered.map(a => (
          <div className="roadmap-row" key={a.slug}>
            <span className={`status ${a.status}`} title={a.status}>{STATUS_ICON[a.status]}</span>
            <div>
              {a.status === 'published' ? (
                <Link to={`/a/${a.slug}`}>{a.title}</Link>
              ) : (
                <span>{a.title}</span>
              )}
              <div style={{ color: 'var(--text-faint)', fontSize: 13 }}>{a.description}</div>
            </div>
            <div className="cat">{cats[a.category].label}</div>
            <div className="diff">{a.difficulty}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
